/**
 * Solar position calculations using SunCalc library
 * Provides accurate dawn/dusk times accounting for latitude and time of year
 */

import SunCalc from 'suncalc';

/**
 * Calculate dawn and dusk times for a given location and date using SunCalc
 * @param {number} latitude - latitude in degrees
 * @param {number} longitude - longitude in degrees
 * @param {Date} date - date to calculate for (defaults to today)
 * @returns {Object} - {dawn: Date, dusk: Date, isAlwaysDay: boolean, isAlwaysNight: boolean}
 */
export const getDawnDusk = (latitude, longitude = 0, date = new Date()) => {
  try {
    const times = SunCalc.getTimes(date, latitude, longitude);
    
    // SunCalc returns actual Date objects for dawn and dusk
    const dawn = times.dawn;
    const dusk = times.dusk;
    
    // Check for invalid times (polar day/night)
    const isValidDawn = dawn && !isNaN(dawn.getTime());
    const isValidDusk = dusk && !isNaN(dusk.getTime());
    
    if (!isValidDawn && !isValidDusk) {
      // Determine if it's polar day or night by checking sun position at noon
      const noon = new Date(date);
      noon.setHours(12, 0, 0, 0);
      const sunPosition = SunCalc.getPosition(noon, latitude, longitude);
      
      return {
        dawn: null,
        dusk: null,
        isAlwaysDay: sunPosition.altitude > 0,
        isAlwaysNight: sunPosition.altitude <= 0
      };
    }
    
    return {
      dawn: isValidDawn ? dawn : null,
      dusk: isValidDusk ? dusk : null,
      isAlwaysDay: false,
      isAlwaysNight: false
    };
  } catch (error) {
    console.error('Error calculating dawn/dusk with SunCalc:', error);
    return {
      dawn: null,
      dusk: null,
      isAlwaysDay: false,
      isAlwaysNight: true
    };
  }
};

/**
 * Check if current time is after dark (between dusk and dawn)
 * @param {number} latitude 
 * @param {number} longitude 
 * @param {Date} currentTime - defaults to now
 * @returns {boolean} true if after dark
 */
export const isAfterDark = (latitude, longitude = 0, currentTime = new Date()) => {
  const { dawn, dusk, isAlwaysDay, isAlwaysNight } = getDawnDusk(latitude, longitude, currentTime);
  
  if (isAlwaysDay) return false;
  if (isAlwaysNight) return true;
  
  // If we don't have valid dawn/dusk times, use sun position
  if (!dawn || !dusk) {
    const sunPosition = SunCalc.getPosition(currentTime, latitude, longitude);
    return sunPosition.altitude <= 0; // Sun is below horizon
  }
  
  // Check if current time is between dusk and dawn
  return currentTime >= dusk || currentTime < dawn;
};

/**
 * Get UV index based on sun position using SunCalc
 * @param {number} latitude 
 * @param {number} longitude 
 * @param {Date} currentTime 
 * @returns {number} UV index (0-11+)
 */
export const calculateUVFromSolarPosition = (latitude, longitude = 0, currentTime = new Date()) => {
  // If it's dark, UV is 0
  if (isAfterDark(latitude, longitude, currentTime)) {
    return 0;
  }
  
  try {
    // Get sun position using SunCalc
    const sunPosition = SunCalc.getPosition(currentTime, latitude, longitude);
    const sunAltitude = sunPosition.altitude; // in radians
    
    // If sun is below horizon, UV is 0
    if (sunAltitude <= 0) {
      return 0;
    }
    
    // Convert altitude to degrees for easier calculation
    const altitudeDegrees = sunAltitude * (180 / Math.PI);
    
    // Calculate UV based on sun altitude (higher sun = higher UV)
    // Sun altitude ranges from 0° (horizon) to ~90° (zenith)
    // UV index typically ranges from 0-11+
    let baseUV = (altitudeDegrees / 90) * 8; // Max UV of 8 at zenith
    
    // Adjust for latitude effect on maximum UV
    const latitudeEffect = Math.cos(Math.abs(latitude) * Math.PI / 180);
    baseUV *= latitudeEffect;
    
    // Add seasonal variation (simplified)
    const dayOfYear = Math.floor((currentTime - new Date(currentTime.getFullYear(), 0, 0)) / 86400000);
    const seasonalFactor = 0.8 + 0.2 * Math.cos(2 * Math.PI * (dayOfYear - 172) / 365);
    baseUV *= seasonalFactor;
    
    // Add some realistic variation
    baseUV *= (0.8 + Math.random() * 0.4);
    
    // Ensure reasonable UV range (0-11)
    return Math.max(0, Math.min(11, Math.round(baseUV)));
    
  } catch (error) {
    console.error('Error calculating UV from solar position:', error);
    return 0;
  }
};

// Backward compatibility export
export const getSunriseSunset = getDawnDusk;